import { Fragment, useEffect, useState } from 'react';
import { useTranslate } from 'react-polyglot';
import { observer } from 'mobx-react';
import { useTheme } from 'styled-components';

import { RoundedButton } from '@genially/design-system';
import { CloseIcon, FulSizeIcon, MinimizeIcon } from '@genially/icons';

import { useHostClientContext } from '../../../HostClientProvider/hooks/useHostClientContext';
import { useHostGameContext } from '../../HostGame.context';
import { bankAnswerTextFromId } from '../../utils/bankAnswerText';
import { buildQuestionAnalytics } from '../../utils/buildQuestionAnalytics';
import { buildSessionAnalytics } from '../../utils/buildSessionAnalytics';
import { buildParticipantUsernames } from '../../utils/sessionAnswers';
import { Sidebar } from '../Sidebar/Sidebar';
import {
  COLLAPSED_WIDTH_UNITS,
  CollapseButton,
  ExpandedHeader,
  ExpandedSubtitle,
  ExpandedTitle,
  Header,
  HeaderTitles,
  PushSpacer,
  ScrollArea,
  StyledBigSubtitle,
} from './AnalyticsResultsSidebar.styled';
import { AnalyticsFullScreen } from './components/AnalyticsFullScreen/AnalyticsFullScreen';
import { SectionTitle } from './components/common/SectionTitle.styled';
import { QuestionAnalyticsFullScreen } from './components/QuestionAnalytics/QuestionAnalyticsFullScreen';
import { QuestionAnalyticsView } from './components/QuestionAnalytics/QuestionAnalyticsView';
import { ResolvedQuestionList } from './components/ResolvedQuestionList/ResolvedQuestionList';
import { SessionKpis } from './components/SessionKpis/SessionKpis';
import { useConnectedUsernames } from './hooks/useConnectedUsernames';
import { useRandomBankQuestion } from './hooks/useRandomBankQuestion';

interface AnalyticsResultsSidebarProps {
  isOpen: boolean;
  /** With a question, the panel reads that question; without one, the whole session. */
  selectedQuestionId?: string;
  onClose: () => void;
}

/**
 * The host's aggregated, anonymous read of the session, shown instead of the ranking when
 * the results mode is `analytics`. An observer: everything below is derived from the live
 * game state, so it keeps up with answers arriving and questions being resolved.
 *
 * Two sibling views share this one panel — the session and a single question — rather than
 * living in two containers: the width, the header, the full-screen mechanism and the
 * mutual exclusion with the individual summary are all the same either way, and there is
 * deliberately no way to navigate between them beyond resolving a question or asking for
 * its answers from inside the genially.
 *
 * Full screen is the same panel widened to the whole stage rather than a second container.
 * There is no close button while expanded, by design, but the panel is also closed from
 * outside — a question summary taking the right edge, the results mode changing — so it
 * collapses on close and always comes back on the side, which is where the host left it.
 */
export const AnalyticsResultsSidebar = observer(
  ({ isOpen, selectedQuestionId, onClose }: AnalyticsResultsSidebarProps) => {
    const t = useTranslate();
    const { size } = useTheme();

    const [isExpanded, setIsExpanded] = useState(false);

    const { client } = useHostClientContext();
    const { interactiveQuestions, viewConnection } = useHostGameContext();

    const connectedUsernames = useConnectedUsernames(client);

    const { interactiveQuestionAnswers } = client.game.asJS;

    const analytics = buildSessionAnalytics({
      interactiveQuestionAnswers,
      interactiveQuestions,
      connectedUsernames,
    });

    const selectedQuestion = selectedQuestionId
      ? interactiveQuestions.find(({ Id }) => Id === selectedQuestionId)
      : undefined;

    // A random question's options are not in the document, so the drawn bank question is
    // fetched (once per bank) to be able to say what each participant picked.
    const bankQuestion = useRandomBankQuestion({
      question: selectedQuestion,
      state: selectedQuestion
        ? interactiveQuestionAnswers[selectedQuestion.Id]
        : undefined,
      assignments: client.game.asJS.randomQuestionAssignments,
    });

    const questionAnalytics = selectedQuestion
      ? buildQuestionAnalytics({
          question: selectedQuestion,
          state: interactiveQuestionAnswers[selectedQuestion.Id],
          participantUsernames: buildParticipantUsernames({
            interactiveQuestionAnswers,
            connectedUsernames,
          }),
          resolveBankAnswerText: bankQuestion
            ? answerId => bankAnswerTextFromId(bankQuestion, answerId, t)
            : undefined,
        })
      : undefined;

    // The host can only leave full screen through the collapse button, but the panel is
    // closed from outside on several paths. Collapsing on the way out is what keeps the
    // big view a deliberate choice rather than something that reappears on its own.
    useEffect(() => {
      if (!isOpen) {
        setIsExpanded(false);
      }
    }, [isOpen]);

    const handleClickExpand = () => {
      setIsExpanded(true);
    };

    const handleClickCollapse = () => {
      setIsExpanded(false);
    };

    // The view echoes the navigation back as `onChangeSlide`, so the players follow the
    // host as if the slide had been changed inside the genially. Full screen covers the
    // genially, so it collapses first: otherwise the host would move to the slide and see
    // nothing happen.
    const handleGoToSlide = (slideId: string) => {
      setIsExpanded(false);

      if (viewConnection.isConnected) {
        viewConnection.connection.changeSlide(slideId);
      }
    };

    const header = isExpanded ? (
      <ExpandedHeader>
        <HeaderTitles>
          <ExpandedTitle>{t('__new.play.analyticsResults.title')}</ExpandedTitle>
          <ExpandedSubtitle>{t('__new.play.analyticsResults.subtitle')}</ExpandedSubtitle>
        </HeaderTitles>

        <CollapseButton>
          <RoundedButton
            Icon={<MinimizeIcon />}
            variant={RoundedButton.Variant.PRIMARY}
            size={RoundedButton.Size.SMALL}
            onClick={handleClickCollapse}
            aria-label={t('__new.play.analyticsResults.minimize')}
          />
        </CollapseButton>
      </ExpandedHeader>
    ) : (
      <Header>
        <RoundedButton
          Icon={<CloseIcon />}
          variant={RoundedButton.Variant.PRIMARY}
          size={RoundedButton.Size.SMALL}
          onClick={onClose}
          aria-label={t('__new.play.analyticsResults.close')}
        />

        {/* The same heading in both views, as the design writes it. Imprecise for a single
            question, but the two views tell themselves apart by their content anyway. */}
        <StyledBigSubtitle>{t('__new.play.analyticsResults.title')}</StyledBigSubtitle>

        <RoundedButton
          Icon={<FulSizeIcon />}
          variant={RoundedButton.Variant.PRIMARY}
          size={RoundedButton.Size.SMALL}
          onClick={handleClickExpand}
          aria-label={t('__new.play.analyticsResults.maximize')}
        />
      </Header>
    );

    const content = (() => {
      if (questionAnalytics) {
        // Keyed by the question: switching to another one starts from scratch — the names
        // covered, the filter cleared and the chart redrawn rather than layered over the
        // previous question's, which is what React does when a key repeats among siblings.
        const key = questionAnalytics.question.Id;

        return isExpanded ? (
          <QuestionAnalyticsFullScreen key={key} analytics={questionAnalytics} />
        ) : (
          <QuestionAnalyticsView key={key} analytics={questionAnalytics} />
        );
      }

      return isExpanded ? (
        <AnalyticsFullScreen analytics={analytics} onGoToSlide={handleGoToSlide} />
      ) : (
        <ScrollArea>
          <SectionTitle>{t('__new.play.analyticsResults.summary.title')}</SectionTitle>
          <SessionKpis kpis={analytics.kpis} />

          <SectionTitle>{t('__new.play.analyticsResults.questions.title')}</SectionTitle>
          <ResolvedQuestionList
            questions={analytics.questions}
            onGoToSlide={handleGoToSlide}
          />
        </ScrollArea>
      );
    })();

    return (
      <Fragment>
        <PushSpacer $isOpen={isOpen} />

        <Sidebar
          isOpen={isOpen}
          onClose={onClose}
          side="right"
          width={isExpanded ? '100%' : size.units(COLLAPSED_WIDTH_UNITS)}
          header={header}
          hideOverlay
        >
          {content}
        </Sidebar>
      </Fragment>
    );
  },
);
